import React, { useState, useEffect } from "react";
import moment from "moment";
import { ToastContainer, toast } from "react-toastify";
import "react-dates/initialize";
import "url-search-params-polyfill";
import "react-toastify/dist/ReactToastify.css";

import { generateQueryString } from "../../utils/queryString";
import { IIndustryEvents } from "../../lib/models/IIndustryEvents";

import IndustryEventResult from "./IndustryEventResult";
import SkeletonCard from "./SkeletonCard";
import getIndustryEvents from "../../lib/api/industryEventsApi";

import Pagination from "../shared/Pagination";
import handleFilterChange from "../../utils/handleFilterChange";
import Filters from "../shared/Filters/Filters";

moment.locale("en-GB");
const dateFormat = "YYYYMMDD";

export default function IndustryEvents({ currentPageRef, pageSize }) {
  const urlParams = new URLSearchParams(window.location.search);

  const dateFrom = urlParams.get("dateFrom");
  const dateTo = urlParams.get("dateTo");

  const initialFilters = {
    page: parseInt(urlParams.get("page")) || 1,
    businessArea: urlParams.get("businessArea") || "",
    region: urlParams.getAll("region"),
    eventType: urlParams.getAll("eventType"),
    dateFrom: dateFrom ? moment(dateFrom, dateFormat).format(dateFormat) : null,
    dateTo: dateTo ? moment(dateTo, dateFormat).format(dateFormat) : null,
    search: urlParams.get("search") || ""
  };

  const [filters, updateFilters] = useState(initialFilters);
  const [loading, updateLoading] = useState(true);

  const [results, updateResults]: [IIndustryEvents, React.Dispatch<any>] =
    useState(null);

  const loadIndustryEvents = () => {
    updateLoading(true);
    getIndustryEvents(
      currentPageRef,
      filters.page,
      filters.businessArea,
      filters.eventType,
      filters.region,
      filters.search,
      filters.dateFrom,
      filters.dateTo
    )
      .then((e) => {
        const clientQueryString = generateQueryString(filters);
        window.history.replaceState(null, null, `?${clientQueryString}`);
        updateResults(e);
        updateLoading(false);
      })
      .catch((e) => {
        console.error(e);
        return toast.error("Something went wrong. Please try again later");
      });
  };

  useEffect(loadIndustryEvents, [filters]);

  const handleFiltering = (filterValue) => {
    handleFilterChange("replace", filterValue, filters, updateFilters);
  };

  const renderResults = () => {
    if (results !== null && !loading) {
      return results.results.map((e, i) => (
        <IndustryEventResult key={e.Id} data={e} />
      ));
    }
    return [...Array(pageSize)].map((e, i) => (
      <SkeletonCard key={i} number={i} page={filters.page} />
    ));
  };

  const renderFilters = () => {
    const typeOptions = results.eventTypeOptions.map((option) => ({
      name: option.text,
      value: option.value,
      isSelected: filters.eventType.includes(option.value)
    }));

    const regionOptions = results.regionsOptions.map((option) => ({
      name: option.text,
      value: option.value,
      isSelected: filters.region.includes(option.value)
    }));

    const businessAreas = results.businessAreaOptions.map((area) => ({
      ...area,
      isSelected: filters.businessArea === area.value
    }));

    return (
      <Filters
        applyFilter={handleFiltering}
        sections={[
          {
            name: "eventType",
            className: "event",
            options: typeOptions
          },
          {
            name: "region",
            className: "region",
            options: regionOptions
          },
          {
            className: "date",
            isDateSection: true
          }
        ]}
        businessAreas={businessAreas}
        searchProps={{
          searchValue: filters.search,
          label: "Events",
          doSearch: loadIndustryEvents
        }}
        dateFilter={{
          startDate: filters.dateFrom
            ? moment(filters.dateFrom, dateFormat)
            : null,
          endDate: filters.dateTo ? moment(filters.dateTo, dateFormat) : null
        }}
      />
    );
  };

  return (
    <>
      <ToastContainer />
      <div className="container main-content">
        {results && renderFilters()}

        <div className="row results">
          {renderResults()}
          {results?.results.length === 0 && (
            <div className="col-12">
              <div
                className="no-results"
                dangerouslySetInnerHTML={{
                  __html: results.noResultsContent
                }}
              ></div>
            </div>
          )}
        </div>

        <nav
          className="row justify-content-center pagination"
          role="navigation"
          aria-label="Pagination navigation"
        >
          {results && (
            <Pagination
              pageSize={pageSize}
              totalItems={results.totalItems}
              currentPage={filters.page}
              updatePage={handleFilterChange}
              filters={filters}
              updateFilters={updateFilters}
            />
          )}
        </nav>
      </div>
    </>
  );
}
